import React, { useEffect, useState } from 'react';
import { CircularProgress, Box } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { Timestamp } from 'firebase/firestore';
import MaintenanceForm from './MaintenanceForm';
import { MaintenanceRecord } from '../../types/maintenance';
import { ExtractedMaintenanceData } from '../../services/aiService';

interface Props {
  extractedData: ExtractedMaintenanceData;
  prepareData: (data: ExtractedMaintenanceData) => Promise<Omit<MaintenanceRecord, 'id'>>;
  onSubmit: (data: Partial<MaintenanceRecord>) => Promise<void>;
  onCancel: () => void;
  vehicles: { id: string; name: string }[];
  serviceProviders: { id: string; name: string }[];
  preSelectedVehicleId?: string;
}

const AsyncMaintenanceForm: React.FC<Props> = ({
  extractedData,
  prepareData,
  onSubmit,
  onCancel,
  vehicles,
  serviceProviders,
  preSelectedVehicleId,
}) => {
  const { currentUser, currentOrganization } = useAuth();
  const [maintenance, setMaintenance] = useState<MaintenanceRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        console.log('Preparing maintenance data...');
        const data = await prepareData(extractedData);
        if (mounted) {
          console.log('Setting maintenance data:', data);
          // Ensure all required fields are set in the maintenance data
          // Validate required fields
          if (!currentOrganization?.id || !currentUser?.id) {
            throw new Error('Missing organization or user data');
          }

          const maintenanceWithRequiredFields: Omit<MaintenanceRecord, 'id'> = {
            vehicleId: preSelectedVehicleId || vehicles[0]?.id || '',
            organizationId: currentOrganization.id,
            status: data.status || 'pending',
            type: data.type || 'repair',
            description: data.description || '',
            costHT: typeof data.costHT === 'number' ? data.costHT : 0,
            costTTC: typeof data.costTTC === 'number' ? data.costTTC : 0,
            openDate: data.openDate || Timestamp.now(),
            closeDate: data.status === 'completed' ? (data.closeDate || Timestamp.now()) : undefined,
            events: Array.isArray(data.events) ? data.events : [],
            documents: Array.isArray(data.documents) ? data.documents : [],
            createdBy: currentUser.id,
            updatedBy: currentUser.id,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            // Optional fields
            serviceProviderId: data.serviceProviderId,
            detailedOperations: data.detailedOperations
          };

          // Add temporary id for form handling
          const maintenanceWithId = {
            ...maintenanceWithRequiredFields,
            id: 'temp-' + Date.now()
          };

          setMaintenance(maintenanceWithId);
        }
      } catch (error) {
        console.error('Error preparing maintenance data:', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [extractedData, prepareData, vehicles, preSelectedVehicleId, currentUser, currentOrganization]);

  if (isLoading || !maintenance) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <MaintenanceForm
      maintenance={maintenance}
      onSubmit={onSubmit}
      onCancel={onCancel}
      vehicles={vehicles}
      serviceProviders={serviceProviders}
      preSelectedVehicleId={preSelectedVehicleId}
    />
  );
};

export default AsyncMaintenanceForm;
